<template>
  <v-layout column class="fullpage">
    <input
      type="file"
      style="display: none"
      ref="uploadVoucherInput"
      multiple
      accept=".pdf"
      @change="handleUploadVoucher"
    />

    <SavingDialog :opened.sync="savingDialogOpened" />

    <WarningDialog
      :opened="unreviewedVouchersWarningDialogOpened"
      :allow-skip="true"
      warning-title="Some pages are not reviewed"
      warning-text='There are some pages that you did not review. Please press "Close" to review the highlighted pages in red or press "Skip" to proceed without reviewing.'
      @close="unreviewedVouchersWarningDialogOpened = false"
      @skip="finishReviewing(true)"
    />

    <WarningDialog
      :opened="erroredVouchersWarningDialogOpened"
      :allow-skip="false"
      warning-title="Some pages need to be corrected"
      warning-text='There are some fields that are invalid or empty. Please press "Close" to review the highlighted pages below in red.'
      @close="erroredVouchersWarningDialogOpened = false"
    />

    <v-dialog v-model="deletedVouchersDialogOpened" max-width="600px">
      <v-card>
        <v-card-title>
          <h3>Deleted Payment Vouchers</h3>
        </v-card-title>

        <v-card-text>
          <v-data-table
            v-if="deletedPaymentVouchers.length > 0"
            :headers="deletedVouchersTableHeaders"
            :items="deletedPaymentVouchers"
            hide-default-footer
          >
            <template v-slot:items="props">
              <td class="text-xs-left">
                {{ getFullPaymentName(props.item) }}
              </td>
              <td class="text-xs-left">
                {{ getVoucherCode(props.item) }}
              </td>
              <td>
                <v-tooltip bottom>
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="restoreDeletedVoucher(props.index)"
                    slot="activator"
                  >
                    <v-icon>restore</v-icon>
                  </v-btn>
                  <span>Restore this payment voucher</span>
                </v-tooltip>
              </td>
            </template>
          </v-data-table>

          <div v-else class="text-center pa-4">
            No deleted vouchers to show
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            flat
            @click="deletedVouchersDialogOpened = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Partial: Alerts -->
    <sc-alert v-if="taxReturn.status == 'action_required'" type="info">
      <span v-if="taxReturn.payment_vouchers.length === 0">
        <b>Instruction:</b> You can add tax payments here or go to the next
        section.
      </span>

      <span v-else>
        <b>Instruction:</b> Please review all payments and adjust payment
        details as necessary.
      </span>

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          @click="finishReviewing()"
          :is-disabled="saving || pdfLoading"
          icon-right="arrow-right"
          data-test-id="delivery-next-payment-vouchers"
        >
          Finished Reviewing
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <sc-alert type="info" v-else>
      <span
        ><b>Note:</b> Payment vouchers cannot be edited once they're marked as
        <b>PAID</b>.</span
      >

      <sc-button-group slot="button-group">
        <sc-button
          type="ghost"
          icon-left="arrow-right"
          @click="next"
          :is-loading="saving"
          :is-disabled="pdfLoading"
          data-testid="delivery-next-button"
        >
          Next
        </sc-button>
      </sc-button-group>
    </sc-alert>

    <v-layout class="outer-container" column>
      <v-layout class="inner-container" row>
        <!-- Filing Instructions Panel (LEFT) -->
        <FilingInstructionsPanel
          v-if="filingInstructionsPosition === 'left'"
          ref="filingInstructionsPanel"
          :taxReturn="taxReturn"
          :position="filingInstructionsPosition"
          :visible="filingInstructionsVisible"
          :highlighted-block-numbers="highlightedBlockNumbers"
          @update:highlighted-block-numbers="
            $emit('update:highlightedBlockNumbers', $event)
          "
          @update:position="$emit('update:filingInstructionsPosition', $event)"
          @update:visible="$emit('update:filingInstructionsVisible', $event)"
        />

        <!-- Partial: Sidebar -->
        <sc-flex class="sidebar" :is-column="true" :gap="8">
          <v-layout align-center justify-space-between>
            <sc-h5>Payments</sc-h5>

            <sc-button
              size="small"
              class="text-none mr-0"
              type="primary"
              @click="addPayment"
              :disabled="taxReturn.status === 'completed' || saving"
              data-test-id="add-payment-button"
            >
              <v-icon small class="white--text">add</v-icon>

              <span>Add</span>
            </sc-button>
          </v-layout>

          <v-alert
            :value="loadingFilingInstructionsAnalysis"
            color="grey"
            class="analyzing-filing-instructions-alert"
          >
            <v-icon small class="material-icons-outlined white--text"
              >info</v-icon
            >
            Analyzing filing instructions for more accurate result...
            <v-progress-linear
              :indeterminate="true"
              color="white"
            ></v-progress-linear>
          </v-alert>

          <ul class="page-list">
            <li
              v-for="(voucher, index) in taxReturn.payment_vouchers"
              :key="voucher.page"
              :class="{
                selected: currentPaymentVoucherIndex === index + 1,
                unreviewed:
                  shouldHighlightUnreviewedVouchers && !voucher.reviewed_by,
                errored:
                  shouldHighlightErroredVouchers && isVoucherInvalid(voucher),
              }"
            >
              <a @click="currentPaymentVoucherIndex = index + 1">
                <v-layout>
                  <v-icon class="mr-2" small>
                    {{
                      shouldHighlightErroredVouchers &&
                      isVoucherInvalid(voucher)
                        ? 'error'
                        : voucher.is_instruction_page
                        ? 'info'
                        : 'bookmark_border'
                    }}
                  </v-icon>
                  <span v-if="voucher.is_instruction_page">
                    Instruction
                  </span>

                  <span v-else>
                    {{ getFullPaymentName(voucher) }}

                    <span
                      v-if="voucher.status === 'analyzing'"
                      class="grey--text body-1"
                    >
                      (analyzing...)
                    </span>

                    <br />
                    <span class="caption text--darken-1 grey--text">
                      {{ getVoucherCode(voucher) }}
                    </span>
                  </span>
                </v-layout>

                <v-layout class="ml-4">
                  <v-chip
                    v-if="voucher.is_paid"
                    label
                    small
                    color="success"
                    style="border-radius: 5px; color: #fff"
                  >
                    Paid
                  </v-chip>

                  <v-chip
                    v-if="voucher.is_archived"
                    label
                    small
                    color="grey"
                    style="border-radius: 5px; color: #fff"
                  >
                    Archived
                  </v-chip>
                </v-layout>
              </a>
            </li>
          </ul>

          <v-btn
            v-if="deletedPaymentVouchers.length > 0"
            class="text-none ma-0 mt-2"
            type="default"
            @click="deletedVouchersDialogOpened = true"
            outline
            color="darkgrey"
          >
            <v-icon small class="mr-2">history</v-icon>
            View deleted payments ({{ deletedPaymentVouchers.length }})
          </v-btn>
        </sc-flex>

        <v-layout column class="pdf-container">
          <!-- Partial: Toolbar -->
          <v-layout class="toolbar" justify-space-between align-center>
            <sc-h4 style="min-width: 0">
              <span
                v-if="currentPaymentVoucher"
                class="d-flex align-center flex-shrink-1"
              >
                <span class="text-truncate flex-shrink-1">
                  {{
                    currentPaymentVoucher.is_instruction_page
                      ? 'Instruction'
                      : getFullPaymentName(currentPaymentVoucher)
                  }}
                </span>

                <span
                  v-if="currentPaymentVoucher.status === 'analyzing'"
                  class="grey--text body-1 pl-2"
                >
                  (analyzing...)
                </span>

                <span
                  class="font-weight-regular grey--text pl-2 text-no-wrap flex-shrink-0"
                >
                  {{ getVoucherCode(currentPaymentVoucher) }}
                </span>
              </span>
            </sc-h4>

            <v-switch
              :input-value="filingInstructionsVisible"
              @change="toggleFilingInstructions"
              class="my-0 py-0 filing-instructions-switch"
              hide-details
            >
              <template v-slot:label>
                <span class="body-2">Show Filing Instructions</span>
              </template>
            </v-switch>
          </v-layout>

          <v-layout
            class="page-container"
            justify-center
            align-center
            v-if="!currentPaymentVoucher"
          >
            <span class="pa-4">
              You can add tax payments here or proceed to the next section.
            </span>
          </v-layout>

          <!-- Partial: Content -->
          <v-layout class="page-container" v-else>
            <v-layout
              id="payment-voucher-scrollable-container"
              class="scrollable-container"
            >
              <v-layout class="pdf-page-container">
                <pdf
                  v-if="currentPaymentVoucher.document_location"
                  @page-loaded="currentPaymentVoucherPdfDataLoaded = true"
                  :src="currentPaymentVoucherPdfData"
                  class="pdf ph-no-capture"
                />

                <v-layout
                  v-else-if="
                    currentPaymentVoucher.is_extracted_from_filing_instructions
                  "
                  class="pa-4"
                  column
                >
                  <div class="upload-voucher-message-container">
                    <v-chip label class="ma-0 mb-2">
                      <v-icon small>info_outline</v-icon>
                      <span class="ml-1"
                        >Extracted from Filing Instructions</span
                      >
                    </v-chip>

                    <div>
                      This tax payment is extracted from the Filing Instructions
                      and might not have an associated voucher. Please refer to
                      the Filing Instructions for details.
                    </div>
                  </div>
                </v-layout>

                <v-layout v-else-if="!pdfPageIndex" class="pa-4" column>
                  <div class="upload-voucher-message-container">
                    <span>
                      This payment does not have an associated voucher. You
                      still can fill out the payment information on the right
                      sidebar for the client to make the necessary payment.
                      Optionally, you can upload an associated voucher below.
                    </span>
                  </div>

                  <v-layout align-center>
                    <v-btn
                      color="primary"
                      class="mx-0 my-3 text-none"
                      @click="selectFileToUploadVoucher"
                      :loading="uploadingPaymentVoucher"
                    >
                      <v-icon class="material-icons-outlined" left>
                        file_upload
                      </v-icon>
                      Upload an associated voucher
                    </v-btn>

                    <div class="ml-3">
                      <i>* Only a single-page PDF is supported.</i>
                    </div>
                  </v-layout>
                </v-layout>

                <v-layout v-else-if="pdfLoading" justify-center align-center>
                  <v-progress-circular
                    class="mt-4"
                    indeterminate
                    color="primary"
                  />
                </v-layout>

                <pdf
                  v-else
                  @page-loaded="pdfLoaded = true"
                  @progress="pdfProgress = $event"
                  :page="pdfPageIndex"
                  :src="pdfData"
                  class="pdf ph-no-capture"
                />
              </v-layout>
            </v-layout>

            <sc-flex
              :is-column="true"
              :gap="16"
              v-if="currentPaymentVoucher"
              class="sidebar--right"
            >
              <v-layout>
                <v-checkbox
                  v-model="currentPaymentVoucher.is_instruction_page"
                  :disabled="!isCurrentPaymentEditable"
                  label="Mark as instruction page"
                  hide-details
                ></v-checkbox>
              </v-layout>
              <v-form
                ref="form"
                v-if="!currentPaymentVoucher.is_instruction_page"
              >
                <v-layout class="my-2">
                  <sc-h3>Payment Details</sc-h3>
                </v-layout>

                <v-alert
                  v-if="currentPaymentVoucher.is_paid"
                  :value="currentPaymentVoucher.is_paid"
                  color="success"
                  class="mt-3 text-xs-left"
                  :style="'border-radius: 5px;'"
                >
                  This voucher is marked as <b>PAID</b>, therefore it cannot be
                  edited.
                </v-alert>

                <v-alert
                  v-else-if="currentPaymentVoucher.is_archived"
                  :value="currentPaymentVoucher.is_archived"
                  color="darkgrey"
                  class="mt-3 text-xs-left"
                  :style="'border-radius: 5px;'"
                >
                  This voucher is <b>ARCHIVED</b>, therefore it cannot be
                  edited.
                </v-alert>

                <sc-flex :is-column="true" :gap="8">
                  <sc-flex :is-column="true" :gap="8" class="form-control">
                    <span
                      >Payment type: <span class="error--text">*</span></span
                    >
                    <v-autocomplete
                      solo
                      hide-details
                      class="mt-1 select-input voucher-id-autocomplete"
                      :menu-props="{
                        contentClass: 'voucher-id-autocomplete-menu',
                      }"
                      placeholder="Select payment type"
                      v-model="currentPaymentVoucher.voucher_id"
                      :items="selectableVoucherType"
                      :item-text="
                        item => `${item.state} ${item.name} (${item.code})`
                      "
                      :item-value="item => item.id"
                      :disabled="!isCurrentPaymentEditable"
                      :rules="
                        currentPaymentVoucher.pay_by_check_disabled
                          ? [rules.required]
                          : undefined
                      "
                      @change="updatePaymentLink"
                    >
                      <template v-slot:item="data">
                        <v-list-tile-content>
                          <v-list-tile-title>
                            <span v-if="data.item.deprecated">
                              <span class="red--text">
                                (Deprecated/Do not use)
                              </span>
                              {{ data.item.state }} {{ data.item.code }}
                            </span>

                            <span v-else>
                              {{ data.item.state }} {{ data.item.name }}
                            </span>
                          </v-list-tile-title>
                          <v-list-tile-sub-title>{{
                            data.item.code
                          }}</v-list-tile-sub-title>
                        </v-list-tile-content>
                      </template>
                    </v-autocomplete>
                  </sc-flex>

                  <sc-flex
                    :is-column="true"
                    :gap="8"
                    class="form-control"
                    v-if="currentPaymentVoucher.voucher_id === 'other'"
                  >
                    <span>Payment name: </span>
                    <v-combobox
                      solo
                      hide-details
                      clearable
                      class="mt-1 select-input"
                      :items="customPaymentNames"
                      item-text="name"
                      item-value="name"
                      :value="currentPaymentVoucher.custom_payment_name"
                      :append-icon="null"
                      :disabled="!isCurrentPaymentEditable"
                      :rules="[rules.required]"
                      @change="addCustomPaymentName($event)"
                    >
                      <v-layout
                        slot="item"
                        slot-scope="props"
                        align-center
                        justify-space-between
                      >
                        <span>
                          {{ props.item.name }}
                        </span>

                        <v-btn
                          icon
                          ripple
                          @click.prevent.stop="
                            deleteCustomPaymentName(props.item)
                          "
                        >
                          <v-icon
                            color="error lighten-1"
                            class="material-icons-outlined"
                            >delete</v-icon
                          >
                        </v-btn>
                      </v-layout>
                    </v-combobox>
                  </sc-flex>

                  <sc-flex :is-column="true">
                    <sc-flex :is-column="true" :gap="8" class="form-control">
                      <span>Quarter: <span class="error--text">*</span></span>

                      <v-alert
                        color="info"
                        class="ai-suggestion"
                        v-if="
                          suggestedQuarter &&
                            suggestedQuarter !== currentPaymentVoucher.quarter
                        "
                        :value="true"
                      >
                        <span
                          >Our A.I suggested the following quarter:
                          <b>{{ suggestedQuarter }}</b>
                        </span>

                        <v-layout align-center>
                          <v-btn
                            @click="
                              updateCurrentVoucher('quarter', suggestedQuarter)
                            "
                            class="text-none ma-0 mt-2"
                            hide-details
                            block
                          >
                            Accept
                          </v-btn>
                        </v-layout>
                      </v-alert>

                      <v-select
                        solo
                        hide-details
                        class="mt-1 select-input"
                        :items="['None', 'Q1', 'Q2', 'Q3', 'Q4']"
                        placeholder="None"
                        :value="currentPaymentVoucher.quarter"
                        @change="updateCurrentVoucher('quarter', $event)"
                        :disabled="!isCurrentPaymentEditable"
                        :error="isEmpty(currentPaymentVoucher.quarter)"
                      />
                    </sc-flex>
                  </sc-flex>

                  <sc-flex :is-column="true">
                    <sc-flex :is-column="true" :gap="8" class="form-control">
                      <span>Due date: <span class="error--text">*</span></span>

                      <v-alert
                        color="info"
                        class="ai-suggestion"
                        v-if="checkShowDueDateSuggestion(matchingPayment)"
                        :value="true"
                      >
                        <span
                          >Our A.I suggested the following due date based on
                          filing instructions: <br />
                          <b>
                            {{
                              matchingPayment.due_date === 'ASAP'
                                ? 'As soon as possible'
                                : formatDate(matchingPayment.due_date)
                            }}
                          </b>
                        </span>

                        <v-layout align-center>
                          <v-btn
                            @click="
                              updateDueDateBasedOnSuggestion(matchingPayment)
                            "
                            class="text-none ma-0 mt-2"
                            hide-details
                            block
                          >
                            Accept
                          </v-btn>
                        </v-layout>
                      </v-alert>

                      <v-text-field
                        v-if="!currentPaymentVoucher.is_due_asap"
                        :key="currentPaymentVoucherIndex"
                        solo
                        hide-details
                        class="mt-1 select-input"
                        placeholder="Select due date"
                        v-model="currentPaymentVoucher.due_date"
                        type="date"
                        :disabled="!isCurrentPaymentEditable"
                        :error="isEmpty(currentPaymentVoucher.due_date)"
                      />

                      <v-text-field
                        v-else
                        solo
                        hide-details
                        class="mt-1 select-input"
                        :disabled="true"
                        value="As soon as possible"
                      />

                      <v-layout
                        align-center
                        v-if="
                          !currentPaymentVoucher.is_due_asap &&
                            matchingPayment &&
                            matchingPayment.due_date ===
                              currentPaymentVoucher.due_date
                        "
                        class="green--text mt-1"
                      >
                        <v-icon color="green" small class="mr-1">check</v-icon>
                        <span>Match with filing instructions</span>
                      </v-layout>
                    </sc-flex>
                    <sc-flex :is-column="true">
                      <v-checkbox
                        hide-details
                        v-model="currentPaymentVoucher.is_due_asap"
                        :disabled="!isCurrentPaymentEditable"
                        label="Due ASAP"
                        data-testid="due-asap-checkbox"
                      ></v-checkbox>
                    </sc-flex>
                  </sc-flex>

                  <sc-flex :is-column="true" :gap="8" class="form-control">
                    <span>Amount:</span>
                    <v-alert
                      color="info"
                      class="ai-suggestion"
                      :value="true"
                      v-if="
                        matchingPayment &&
                          matchingPayment.amount !== null &&
                          matchingPayment.amount !==
                            currentPaymentVoucher.amount
                      "
                    >
                      <span
                        >Our A.I suggested the following amount based on filing
                        instructions:
                        <b>{{ formatCurrency(matchingPayment.amount) }}</b>
                      </span>

                      <v-layout align-center>
                        <v-btn
                          @click="
                            updateCurrentVoucher(
                              'amount',
                              matchingPayment.amount,
                            )
                          "
                          class="text-none ma-0 mt-2"
                          hide-details
                          block
                        >
                          Accept
                        </v-btn>
                      </v-layout>
                    </v-alert>
                    <CurrencyInput
                      v-model="currentPaymentVoucher.amount"
                      :options="{ currency: 'USD' }"
                      :disabled="!isCurrentPaymentEditable"
                      :class="{
                        'error--text':
                          currentPaymentVoucher.amount === 0 ||
                          currentPaymentVoucher.amount === null,
                      }"
                    />

                    <v-layout
                      align-center
                      v-if="
                        matchingPayment &&
                          matchingPayment.amount ===
                            currentPaymentVoucher.amount
                      "
                      class="green--text mt-1"
                    >
                      <v-icon color="green" small class="mr-1">check</v-icon>
                      <span>Match with filing instructions</span>
                    </v-layout>

                    <sc-flex :is-column="true">
                      <v-checkbox
                        hide-details
                        v-model="currentPaymentVoucher.pay_by_check_disabled"
                        :disabled="
                          !isCurrentPaymentEditable ||
                            isPayByCheckCheckboxDisabled
                        "
                        label="Disable Pay by Check"
                      ></v-checkbox>
                    </sc-flex>
                  </sc-flex>
                  <sc-flex :is-column="true" :gap="8" class="form-control">
                    <span
                      >Payment link:
                      <span
                        v-if="currentPaymentVoucher.pay_by_check_disabled"
                        class="error--text"
                        >*</span
                      >
                    </span>
                    <v-text-field
                      solo
                      hide-details
                      placeholder="https://"
                      v-model="currentPaymentVoucher.payment_link"
                      class="mt-0 mb-0 select-input"
                      :disabled="!isCurrentPaymentEditable"
                      :rules="[rules.required]"
                      @blur="onBlurOfPaymentLink"
                    />
                  </sc-flex>

                  <sc-flex :is-column="true" :gap="8" class="form-control">
                    <span>Payment instruction link:</span>
                    <v-text-field
                      solo
                      hide-details
                      placeholder="https://"
                      v-model="currentPaymentVoucher.payment_instruction_link"
                      class="mt-0 mb-0 select-input"
                      :disabled="!isCurrentPaymentEditable"
                      @blur="onBlurOfPaymentInstructionLink"
                    />
                  </sc-flex>

                  <v-divider class="mt-4" />

                  <v-layout class="mt-2" column>
                    <v-layout>
                      <v-btn
                        v-if="!currentPaymentVoucher.is_archived"
                        color="darkgrey"
                        class="text-none"
                        block
                        flat
                        @click="archivePayment(currentPaymentVoucherIndex - 1)"
                        :disabled="currentPaymentVoucher.is_paid"
                      >
                        <v-icon left class="material-icons-outlined"
                          >inventory_2</v-icon
                        >
                        Archive this payment
                      </v-btn>

                      <v-btn
                        v-else
                        color="darkgrey"
                        class="text-none"
                        block
                        flat
                        @click="
                          unarchivePayment(currentPaymentVoucherIndex - 1)
                        "
                        :disabled="currentPaymentVoucher.is_paid"
                      >
                        <v-icon left class="material-icons-outlined"
                          >restore</v-icon
                        >
                        Unarchive this payment
                      </v-btn>
                    </v-layout>

                    <v-layout>
                      <v-btn
                        color="error"
                        class="text-none"
                        block
                        flat
                        @click="removePayment"
                        :disabled="!isCurrentPaymentEditable"
                      >
                        <v-icon left class="material-icons-outlined"
                          >delete</v-icon
                        >
                        Remove this payment
                      </v-btn>
                    </v-layout>
                  </v-layout>
                </sc-flex>
              </v-form>
              <v-alert
                v-else
                type="info"
                class="mt-3 text-xs-left"
                :style="'border-radius: 5px;'"
                :value="true"
                outline
              >
                <span
                  >Instruction page will be shown in the payment voucher
                  section, but without any payment details.</span
                >
              </v-alert>
              <v-layout>
                <v-btn
                  color="primary"
                  class="text-none"
                  block
                  flat
                  @click="nextVoucher"
                  :disabled="!isCurrentPaymentEditable"
                >
                  <v-icon left class="material-icons-outlined"
                    >arrow_forward</v-icon
                  >
                  Next Voucher
                </v-btn>
              </v-layout>
            </sc-flex>
          </v-layout>
        </v-layout>

        <!-- Filing Instructions Panel (RIGHT) -->
        <FilingInstructionsPanel
          v-if="filingInstructionsPosition === 'right'"
          ref="filingInstructionsPanel"
          :taxReturn="taxReturn"
          :position="filingInstructionsPosition"
          :visible="filingInstructionsVisible"
          :highlighted-block-numbers="highlightedBlockNumbers"
          @update:highlighted-block-numbers="
            $emit('update:highlightedBlockNumbers', $event)
          "
          @update:position="$emit('update:filingInstructionsPosition', $event)"
          @update:visible="$emit('update:filingInstructionsVisible', $event)"
        />
      </v-layout>

      <!-- Filing Instructions BOTTOM -->
      <FilingInstructionsPanel
        v-if="filingInstructionsPosition === 'bottom'"
        ref="filingInstructionsPanel"
        :taxReturn="taxReturn"
        :position="filingInstructionsPosition"
        :visible="filingInstructionsVisible"
        :highlighted-block-numbers="highlightedBlockNumbers"
        @update:highlighted-block-numbers="
          $emit('update:highlightedBlockNumbers', $event)
        "
        @update:position="$emit('update:filingInstructionsPosition', $event)"
        @update:visible="$emit('update:filingInstructionsVisible', $event)"
      />
    </v-layout>
  </v-layout>
</template>

<script>
import pdf from 'vue-pdf';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import { Sema } from 'async-sema';
import { PDFDocument } from 'pdf-lib';
import ReadonlyAlert from '@/components/returns/ReadonlyAlert';
import CurrencyInput from '@/components/returns/CurrencyInput';
import WarningDialog from '@/components/returns/WarningDialog';
import SavingDialog from '@/components/returns/SavingDialog';
import chunk from 'lodash/chunk';
import FilingInstructionsPanel from '@/components/returns/FilingInstructionsPanel';
import FilingInstructionsPanelMixin from '@/mixins/FilingInstructionsPanelMixin';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import moment from 'moment';

export default {
  components: {
    pdf,
    ReadonlyAlert,
    CurrencyInput,
    WarningDialog,
    SavingDialog,
    FilingInstructionsPanel,
  },

  mixins: [FilingInstructionsPanelMixin, FirmPreferenceMixins],

  props: {
    currentCompany: {
      type: Object,
      default: () => {},
    },

    taxReturn: {
      type: Object,
      default: () => {},
    },

    filingInstructionsPosition: {
      type: String,
    },

    filingInstructionsVisible: {
      type: Boolean,
      default: false,
    },

    highlightedBlockNumbers: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      currentPaymentVoucherIndex: null,

      pdfProgress: 0,
      pdfLoaded: false,
      pdfData: null,
      pdfDoc: null,
      pdfLoading: false,
      pdfPageIndex: null,

      saving: false,

      // States for forms with errors
      erroredVouchersWarningDialogOpened: false,
      shouldHighlightErroredVouchers: false,

      // States for unreviewed forms
      unreviewedVouchersWarningDialogOpened: false,
      shouldHighlightUnreviewedVouchers: false,

      savingDialogOpened: false,

      selectableVoucherType: [],
      customPaymentNames: [],

      rules: {
        required: value => !!value || 'Required.',
      },

      uploadVoucherInput: null,
      uploadingPaymentVoucher: false,
      currentPaymentVoucherPdfData: null,

      filingInstructionsAnalysisResult: null,
      loadingFilingInstructionsAnalysis: false,

      deletedPaymentVouchers: [],
      deletedVouchersDialogOpened: false,
      deletedVouchersTableHeaders: [
        { text: 'Payment Name', value: 'payment_name', align: 'left' },
        { text: 'Code', value: 'voucher_code', align: 'left' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentUserFirm: 'currentFirm',
    }),

    currentPaymentVoucher() {
      if (this.currentPaymentVoucherIndex === null) {
        return null;
      } else {
        return this.taxReturn.payment_vouchers[
          this.currentPaymentVoucherIndex - 1
        ];
      }
    },

    suggestedQuarter() {
      if (!this.filingInstructionsAnalysisResult) {
        return;
      }

      const currentPaymentVoucher = this.currentPaymentVoucher;

      if (
        !currentPaymentVoucher.voucher_id ||
        currentPaymentVoucher.voucher_id === 'unknown'
      ) {
        return;
      }

      const matchingPayments = this.filingInstructionsAnalysisResult.payment_infos.filter(
        pi => {
          return pi.voucher_id === currentPaymentVoucher.voucher_id;
        },
      );

      if (matchingPayments.length === 1) {
        return matchingPayments[0].quarter;
      } else if (matchingPayments.length > 1) {
        // Check by amount (only return if there is an unique match)
        if (!isEmpty(currentPaymentVoucher.amount)) {
          const paymentsWithMatchingAmount = matchingPayments.filter(
            pi => pi.amount === currentPaymentVoucher.amount,
          );

          if (paymentsWithMatchingAmount.length === 1) {
            return paymentsWithMatchingAmount[0].quarter;
          }
        }

        // Check by due date
        /* We don't need this at this moment
        if (!isEmpty(currentPaymentVoucher.due_date)) {
          const matchingPayment = matchingPayments.find((pi) => pi.due_date === currentPaymentVoucher.due_date)

          if (matchingPayment) {
            return matchingPayment.quarter
          }
        }
        */

        // Get the positions of vouchers with the same id in `taxReturn.payment_vouchers`
        // For example, for four 1040-ES payment vouchers, this would return positions [17, 18, 19, 20]
        const sameVoucherPositions = this.taxReturn.payment_vouchers.reduce(
          (acc, curr, index) => {
            if (curr.voucher_id === currentPaymentVoucher.voucher_id) {
              return acc.concat(index);
            } else {
              return acc;
            }
          },
          [],
        );

        // Based on the positions, we determine the index of the current voucher.
        // For example, a voucher at position 19 above would return an index of 2.
        // This index can be used to predict the 'quarter' in the code below.
        // Note: we subtract 1 because currentPaymentVoucherIndex is 1-based.
        const expectedPaymentIndex = sameVoucherPositions.findIndex(
          voucherIndex => voucherIndex === this.currentPaymentVoucherIndex - 1,
        );

        if (sameVoucherPositions.length === matchingPayments.length) {
          return matchingPayments[expectedPaymentIndex].quarter;
        }
        // This occurs when there are fewer vouchers than payments in the filing instructions.
        // In this case, we need to filter out payments of 0 since these don't have vouchers.
        else if (sameVoucherPositions.length < matchingPayments.length) {
          const paymentsWithNonZeroAmount = matchingPayments.filter(
            pi => pi.amount > 0,
          );

          if (
            sameVoucherPositions.length === paymentsWithNonZeroAmount.length
          ) {
            return paymentsWithNonZeroAmount[expectedPaymentIndex].quarter;
          }
        }
      }
    },

    // return the payment information from the filing instructions that match the current payment voucher
    matchingPayment() {
      if (!this.filingInstructionsAnalysisResult) {
        return;
      }

      const currentPaymentVoucher = this.currentPaymentVoucher;

      if (
        !currentPaymentVoucher ||
        !currentPaymentVoucher.voucher_id ||
        currentPaymentVoucher.voucher_id === 'unknown'
      ) {
        return;
      }

      const matchingPayments = this.filingInstructionsAnalysisResult.payment_infos.filter(
        pi => {
          return pi.voucher_id === currentPaymentVoucher.voucher_id;
        },
      );

      let matchingPayment;

      if (matchingPayments.length === 1) {
        matchingPayment = matchingPayments[0];
      } else if (matchingPayments.length > 1) {
        matchingPayment = matchingPayments.find(
          pi => pi.quarter === currentPaymentVoucher.quarter,
        );
      }

      return matchingPayment;
    },

    isCurrentPaymentEditable() {
      return (
        this.taxReturn.status !== 'completed' &&
        !this.currentPaymentVoucher.is_paid &&
        !this.currentPaymentVoucher.is_archived
      );
    },

    // Disabled when the voucher does not have a page or does not have an associated document
    isPayByCheckCheckboxDisabled() {
      return (
        this.currentPaymentVoucher.page === null &&
        !this.currentPaymentVoucher.document_location
      );
    },
  },

  methods: {
    isEmpty,

    checkShowDueDateSuggestion(suggestion) {
      if (!suggestion || !suggestion.due_date) {
        return false;
      }

      if (suggestion.due_date === 'ASAP') {
        if (!this.currentPaymentVoucher.is_due_asap) {
          return true;
        } else {
          return false;
        }
      } else if (suggestion.due_date !== this.currentPaymentVoucher.due_date) {
        return true;
      }

      return false;
    },

    updateDueDateBasedOnSuggestion(suggestion) {
      if (suggestion.due_date === 'ASAP') {
        this.updateCurrentVoucher('is_due_asap', true);
      } else {
        this.updateCurrentVoucher('due_date', suggestion.due_date);
        this.updateCurrentVoucher('is_due_asap', false);
      }
    },

    formatDate(date) {
      if (!date) return '';
      return moment(date).format('MM/DD/YYYY');
    },

    formatCurrency(amount) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      return formatter.format(amount);
    },

    nextVoucher() {
      this.currentPaymentVoucherIndex = this.currentPaymentVoucherIndex + 1;
    },

    updateCurrentPaymentVoucherIndex($event) {
      let newIndex = parseInt($event.target.value);

      // Make sure the index is not out of bounds
      if (newIndex < 1) {
        newIndex = 1;
      } else if (newIndex > this.taxReturn.payment_vouchers.length) {
        newIndex = this.taxReturn.payment_vouchers.length;
      }

      this.currentPaymentVoucherIndex = newIndex;
      $event.target.value = newIndex;
    },

    async showSavingDialog() {
      try {
        this.savingDialogOpened = true;
        const taxReturn = await this.save();

        if (taxReturn) {
          this.$emit('payment-vouchers-updated', taxReturn);
        }
      } finally {
        this.savingDialogOpened = false;
      }
    },

    async beforeClose() {
      if (this.savingDialogOpened) {
        return;
      }

      await this.showSavingDialog();
      return true;
    },

    async save() {
      const response = await axios.patch(`/tax_returns/${this.taxReturn.id}`, {
        company_id: this.taxReturn.company_id,
        payment_vouchers: this.taxReturn.payment_vouchers,
      });

      return response.data;
    },

    async finishReviewing(skipUnreviewed = false) {
      if (this.hasErrors()) {
        this.erroredVouchersWarningDialogOpened = true;
        this.shouldHighlightErroredVouchers = true;
        this.validateInputs();
        return;
      }

      if (!skipUnreviewed && this.hasUnreviewedVouchers()) {
        this.unreviewedVouchersWarningDialogOpened = true;
        this.shouldHighlightUnreviewedVouchers = true;
        return;
      }

      const taxReturn = await this.save();

      this.$emit('payment-vouchers-updated', taxReturn);
    },

    getVoucherDefinitionById(id) {
      return this.selectableVoucherType.find(voucher => voucher.id === id);
    },

    async getVoucherDefinitions() {
      const response = await axios.get(`/tax_returns/get_voucher_definitions`);
      this.selectableVoucherType = response.data;
    },

    async getVoucherInfo(page) {
      const response = await axios.post(
        `/tax_returns/${this.taxReturn.id}/analyze_payment_voucher`,
        {
          company_id: this.currentCompany.id,
          page,
        },
      );

      return response.data;
    },

    getFullPaymentName(paymentVoucher) {
      let paymentName =
        paymentVoucher.voucher_id === 'other'
          ? paymentVoucher.custom_payment_name
          : this.getVoucherDefinitionById(paymentVoucher.voucher_id)
          ? `${
              this.getVoucherDefinitionById(paymentVoucher.voucher_id).state
            } ${this.getVoucherDefinitionById(paymentVoucher.voucher_id).name}`
          : 'Unknown';

      if (paymentVoucher.quarter && paymentVoucher.quarter !== 'None') {
        paymentName += ` (${paymentVoucher.quarter})`;
      }

      return paymentName;
    },

    getVoucherCode(paymentVoucher) {
      return paymentVoucher.voucher_id === 'other'
        ? ''
        : this.getVoucherDefinitionById(paymentVoucher.voucher_id)
        ? this.getVoucherDefinitionById(paymentVoucher.voucher_id).code
        : '';
    },

    next() {
      this.$emit('next');
    },

    async analyzeAllPaymentVouchers() {
      const vouchersToAnalyze = this.taxReturn.payment_vouchers
        .filter(voucher => {
          return (
            voucher.voucher_id === 'unknown' && voucher.status !== 'analyzed'
          );
        })
        .map(voucher => {
          voucher.status = 'analyzing';
          return voucher;
        });

      const sema = new Sema(4); // Limit to 4 concurrent requests

      const analyzeVoucher = async voucherPage => {
        await sema.acquire();

        const pages = [voucherPage];

        try {
          const response = await axios.post(
            `/tax_returns/${this.taxReturn.id}/analyze_payment_vouchers`,
            {
              company_id: this.currentCompany.id,
              pages,
              model: 'gemini-2.5-pro',
            },
          );

          response.data.forEach(result => {
            if (voucherPage === result.page) {
              // Here we find the correct payment voucher object to update
              const voucher = this.taxReturn.payment_vouchers.find(
                v => v.page === result.page,
              );

              if (voucher) {
                try {
                  const extractedInfo = result.extracted_info;

                  if (extractedInfo !== 'unknown') {
                    voucher.voucher_id = extractedInfo.form_id;

                    if (!isEmpty(extractedInfo.quarter)) {
                      voucher.quarter = extractedInfo.quarter;
                    } else {
                      voucher.quarter = 'None';
                    }

                    voucher.due_date = extractedInfo.due_date;
                    if (isEmpty(voucher.amount)) {
                      voucher.amount = extractedInfo.due_amount;
                    }

                    const voucherDefinition = this.getVoucherDefinitionById(
                      voucher.voucher_id,
                    );

                    if (voucherDefinition) {
                      // Add default payment link
                      voucher.payment_link = voucherDefinition.payment_link;

                      // Add default due date if not set
                      if (
                        isEmpty(voucher.due_date) &&
                        Array.isArray(voucherDefinition.default_due_dates)
                      ) {
                        // defaultDueDate is { day: number, month: number } format
                        const defaultDueDate = voucherDefinition.default_due_dates.find(
                          date => date.quarter === voucher.quarter,
                        );

                        if (defaultDueDate) {
                          const due_date = moment({
                            year: this.taxReturn.tax_year + 1,
                            month: defaultDueDate.month - 1, // month is 0-indexed in moment
                            day: defaultDueDate.day,
                          });

                          // Only set if the due date is in the future
                          if (due_date.isAfter()) {
                            voucher.due_date = due_date.format('YYYY-MM-DD');
                          }
                        }
                      }
                    }
                  }

                  voucher.status = 'analyzed';
                } catch (error) {
                  voucher.status = 'analyze_failed';
                }
              }
            }
          });
        } catch (error) {
          voucher.status = 'analyze_failed';
        } finally {
          sema.release();
        }
      };

      const analyzeTasks = vouchersToAnalyze.map(voucher =>
        analyzeVoucher(voucher.page),
      );

      await Promise.all(analyzeTasks);

      // Wait until filing instructions analysis is done
      while (this.loadingFilingInstructionsAnalysis) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      if (this.filingInstructionsAnalysisResult) {
        this.addRemainingVouchersFromFilingInstructions();
      }
    },

    isDueASAP(dueDate) {
      return this.currentPaymentVoucher.due_date === 'ASAP';
    },

    scrollToBottom() {
      const scrollableContainer = document.querySelector(
        '#payment-voucher-scrollable-container',
      );

      if (scrollableContainer) {
        scrollableContainer.scrollTo({
          top: scrollableContainer.scrollHeight,
          behavior: 'smooth',
        });
      }
    },

    getUnreviewedVouchers() {
      return this.taxReturn.payment_vouchers.filter(voucher => {
        return !voucher.reviewed_by;
      });
    },

    hasUnreviewedVouchers() {
      return this.getUnreviewedVouchers().length > 0;
    },

    hasErrors() {
      return this.taxReturn.payment_vouchers.some(voucher => {
        return this.isVoucherInvalid(voucher);
      });
    },

    async markCurrentPaymentVoucherReviewed() {
      this.currentPaymentVoucher.reviewed_by = this.currentUser.id;
      await this.save();
    },

    isVoucherInvalid(voucher) {
      if (voucher.is_instruction_page) {
        return false;
      }
      if (voucher.voucher_id === 'unknown' || isEmpty(voucher.voucher_id)) {
        return true;
      } else if (isEmpty(voucher.due_date) && !voucher.is_due_asap) {
        return true;
      } else if (isEmpty(voucher.quarter)) {
        return true;
      } else if (
        voucher.pay_by_check_disabled &&
        isEmpty(voucher.payment_link)
      ) {
        return true;
      } else {
        return false;
      }
    },

    validateInputs() {
      this.$refs.form.validate();
    },

    async getCustomPaymentNames() {
      const response = await API.firm.getCustomPaymentNames({
        firmId: this.currentUserFirm.id,
      });

      this.customPaymentNames = response.data;
    },

    async addCustomPaymentName(item) {
      // Depends on whether item is selected or typed, it can be either an object or just a string;
      const name = item && item.name ? item.name : item;

      this.currentPaymentVoucher.custom_payment_name = name;

      if (isEmpty(name)) {
        return;
      }

      const response = await API.firm.addCustomPaymentName({
        firmId: this.currentUserFirm.id,
        name,
      });

      this.customPaymentNames.push(response.data);
    },

    async deleteCustomPaymentName(item) {
      const response = await API.firm.deleteCustomPaymentName({
        firmId: this.currentUserFirm.id,
        id: item.id,
      });

      this.customPaymentNames = this.customPaymentNames.filter(
        customPaymentName => customPaymentName.id !== item.id,
      );
    },

    async addPayment() {
      const newPayment = {
        voucher_id: 'unknown',
        page: null,
        due_date: '',
        quarter: 'None',
        amount: 0,
        payment_link: '',
        payment_instruction_link: '',
        reviewed_by: null,
        status: 'analyzed',
        pay_by_check_disabled: false,
        is_instruction_page: false,
      };

      this.taxReturn.payment_vouchers.push(newPayment);

      this.currentPaymentVoucherIndex = this.taxReturn.payment_vouchers.length;
    },

    async removePayment() {
      const deletedVoucher = this.taxReturn.payment_vouchers[
        this.currentPaymentVoucherIndex - 1
      ];
      this.deletedPaymentVouchers.push(deletedVoucher);
      this.taxReturn.payment_vouchers.splice(
        this.currentPaymentVoucherIndex - 1,
        1,
      );
      this.currentPaymentVoucherIndex = this.currentPaymentVoucherIndex - 1;
      await this.save();
    },

    async archivePayment(index) {
      this.currentPaymentVoucher.is_archived = true;
      await this.save();

      // Unsure why this is necessary, but without it, the update doesn't trigger a re-render
      this.$forceUpdate();
    },

    async unarchivePayment(index) {
      this.currentPaymentVoucher.is_archived = false;
      await this.save();

      // Unsure why this is necessary, but without it, the update doesn't trigger a re-render
      this.$forceUpdate();
    },

    async updatePaymentLink() {
      const voucherDefinition = this.getVoucherDefinitionById(
        this.currentPaymentVoucher.voucher_id,
      );

      if (voucherDefinition && voucherDefinition.payment_link) {
        this.currentPaymentVoucher.payment_link =
          voucherDefinition.payment_link;
      } else {
        this.currentPaymentVoucher.payment_link = '';
      }
    },

    selectFileToUploadVoucher() {
      this.$refs.uploadVoucherInput.click();
    },

    async handleUploadVoucher($e) {
      const currentPaymentIndex = this.currentPaymentVoucherIndex - 1;

      this.uploadingPaymentVoucher = true;

      try {
        const file = $e.target.files[0];

        // Check if this is a single-page PDF
        const pdfDoc = await PDFDocument.load(await file.arrayBuffer());
        const pdfData = await pdfDoc.saveAsBase64({ dataUri: true });

        if (pdfDoc.getPageCount() !== 1) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Please upload a single-page pdf.',
          });

          return;
        }

        const { documentName, documentLocation } = await this.uploadVoucher(
          file,
        );

        this.$set(this.taxReturn.payment_vouchers, currentPaymentIndex, {
          ...this.taxReturn.payment_vouchers[currentPaymentIndex],
          document_location: documentLocation,
        });

        await this.save();
      } finally {
        this.uploadingPaymentVoucher = false;
      }
    },

    async uploadVoucher(file) {
      const {
        data: { id, upload_link },
      } = await API.taxReturn.getUploadLinkForPaymentVoucher({
        companyId: this.currentCompany.id,
      });

      await API.integration.uploadToDropbox(upload_link, file);

      const response = await API.taxReturn.processPaymentVoucher({
        fileId: id,
        taxReturnId: this.taxReturn.id,
        documentName: file.name,
        companyId: this.currentCompany.id,
      });

      return {
        documentName: response.data.document_name,
        documentLocation: response.data.document_location,
      };
    },

    onBlurOfPaymentLink(event) {
      const { value } = event.target;

      if (value && !value.trim().startsWith('http')) {
        this.currentPaymentVoucher.payment_link = 'https://' + value.trim();
      }
    },

    onBlurOfPaymentInstructionLink(event) {
      const { value } = event.target;

      if (value && !value.trim().startsWith('http')) {
        this.currentPaymentVoucher.payment_instruction_link =
          'https://' + value.trim();
      }
    },

    async loadFilingInstructionsAnalysis() {
      this.loadingFilingInstructionsAnalysis = true;

      try {
        const maxRetries = 100; // 5 minutes / 3 seconds = 100 retries
        const retryInterval = 3000; // 3 seconds in milliseconds
        let retryCount = 0;

        const attemptLoad = async () => {
          try {
            const response = await API.taxReturn.analyzeFilingInstructions({
              id: this.taxReturn.id,
              companyId: this.taxReturn.company_id,
              pages: this.taxReturn.filing_instructions.map(fi => fi.page),
            });
            this.filingInstructionsAnalysisResult = response.data;
          } catch (error) {
            if (error.response && error.response.status === 425) {
              // 425 status code means not ready yet, retry
              if (retryCount < maxRetries) {
                retryCount++;
                await new Promise(resolve =>
                  setTimeout(resolve, retryInterval),
                );
                await attemptLoad();
              } else {
                console.error(
                  'Filing instructions analysis failed after max retries:',
                  error,
                );
              }
            } else {
              console.error(
                'Error loading filing instructions analysis:',
                error,
              );
            }
          }
        };

        await attemptLoad();
      } finally {
        this.loadingFilingInstructionsAnalysis = false;
      }
    },

    addRemainingVouchersFromFilingInstructions() {
      // Do not add new vouchers from filing instructions if the tax return has been delivered
      if (this.taxReturn.status !== 'action_required') {
        return;
      }

      const paymentsToAdd = [];

      for (const paymentInfo of this.filingInstructionsAnalysisResult
        .payment_infos) {
        const alreadyExists = this.taxReturn.payment_vouchers.some(
          voucher => voucher.voucher_id === paymentInfo.voucher_id,
        );

        if (alreadyExists) {
          continue;
        }

        // If no payment allowed_payment_methods allowed, it likely means it's automatically withdrawn from the taxpayer's account,
        // so we can skip it
        if (paymentInfo.allowed_payment_methods.length === 0) {
          continue;
        }

        // 'null' means that the filing instructions doesn't mention how much
        // 0 means that this payment is not needed (actual 0) so we skip it
        if (paymentInfo.amount === 0) {
          continue;
        }

        paymentsToAdd.push(paymentInfo);
      }

      for (const newPayment of paymentsToAdd) {
        const voucherDefinition = this.getVoucherDefinitionById(
          newPayment.voucher_id,
        );

        const paymentLink = voucherDefinition
          ? voucherDefinition.payment_link
          : '';

        this.taxReturn.payment_vouchers.push({
          voucher_id: newPayment.voucher_id,
          due_date:
            newPayment.due_date === 'ASAP'
              ? moment().format('YYYY-MM-DD')
              : newPayment.due_date,
          is_due_asap: newPayment.due_date === 'ASAP',
          // If null, we temporarily set the amount to 0 so that the voucher gets added. The user can then update it if needed.
          amount: newPayment.amount === null ? 0 : newPayment.amount,
          quarter: newPayment.quarter,
          payment_link: paymentLink,
          payment_instruction_link: '',
          reviewed_by: null,
          status: 'analyzed',
          pay_by_check_disabled: !newPayment.allowed_payment_methods.includes(
            'check',
          ),
          is_instruction_page: false,
          is_extracted_from_filing_instructions: true,
        });
      }
    },

    updateCurrentVoucher(field_name, value) {
      this.$set(
        this.taxReturn.payment_vouchers,
        this.currentPaymentVoucherIndex - 1,
        {
          ...this.taxReturn.payment_vouchers[
            this.currentPaymentVoucherIndex - 1
          ],
          [field_name]: value,
        },
      );
    },

    async restoreDeletedVoucher(index) {
      const restoredVoucher = this.deletedPaymentVouchers[index];

      this.taxReturn.payment_vouchers.push(restoredVoucher);

      // Sort by payments by page, so that the deleted payment is placed back in the correct order.
      this.taxReturn.payment_vouchers = this.taxReturn.payment_vouchers.sort(
        (a, b) => {
          if (typeof a.page === 'undefined' || a.page === null) return 1;
          if (typeof b.page === 'undefined' || b.page === null) return -1;
          return a.page - b.page;
        },
      );

      this.deletedPaymentVouchers.splice(index, 1);
      await this.save();
    },
  },

  watch: {
    async currentPaymentVoucherIndex() {
      if (this.currentPaymentVoucher) {
        // Set quarter to "None" if it's empty
        if (isEmpty(this.currentPaymentVoucher.quarter)) {
          this.updateCurrentVoucher('quarter', 'None');
        }

        if (this.currentPaymentVoucher.page) {
          this.pdfPageIndex = this.currentPaymentVoucher.page;
          this.scrollToBottom();
        } else {
          this.pdfPageIndex = null;
        }

        if (
          this.currentPaymentVoucher.page === null &&
          !this.currentPaymentVoucher.document_location
        ) {
          this.currentPaymentVoucher.pay_by_check_disabled = true;
        }

        await this.markCurrentPaymentVoucherReviewed();
      }
    },

    matchingPayment: {
      handler(value) {
        if (value && !isEmpty(value.referenced_block_numbers)) {
          this.$emit(
            'update:highlightedBlockNumbers',
            value.referenced_block_numbers,
          );
        } else {
          this.$emit('update:highlightedBlockNumbers', []);
        }
      },
    },

    async 'currentPaymentVoucher.document_location'(newValue) {
      this.currentPaymentVoucherPdfData = null;
      this.currentPaymentVoucherPdfDataLoaded = false;

      if (newValue) {
        const response = await API.integration.downloadDocument({
          companyId: this.currentCompany.id,
          link: this.currentPaymentVoucher.document_location,
        });

        const pdfSrc = response.data.document.data.link;
        const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

        const pdfDoc = await PDFDocument.load(pdfBytes);
        this.currentPaymentVoucherPdfData = await pdfDoc.saveAsBase64({
          dataUri: true,
        });
      }
    },

    async pdfLoaded() {
      if (this.pdfLoaded) {
        this.scrollToBottom();
        await this.markCurrentPaymentVoucherReviewed();
      }
    },
  },

  async beforeDestroy() {
    await this.beforeClose();
  },

  async mounted() {
    await this.getVoucherDefinitions();

    this.getCustomPaymentNames();

    // Load filing instructions analysis
    if (
      this.taxReturnUseFilingInstructionsParser &&
      this.taxReturn.filing_instructions &&
      this.taxReturn.filing_instructions.length > 0
    ) {
      this.loadFilingInstructionsAnalysis();
    }

    try {
      this.pdfLoading = true;

      const response = await API.integration.downloadDocument({
        companyId: this.currentCompany.id,
        link: this.taxReturn.original_file,
      });

      const pdfSrc = response.data.document.data.link;
      const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

      this.pdfDoc = await PDFDocument.load(pdfBytes);
      this.pdfData = await this.pdfDoc.saveAsBase64({ dataUri: true });

      if (this.taxReturn.payment_vouchers.length > 0) {
        this.currentPaymentVoucherIndex = 1;

        this.pdfPageIndex = this.taxReturn.payment_vouchers[
          this.currentPaymentVoucherIndex - 1
        ].page;
      }

      this.analyzeAllPaymentVouchers();
    } finally {
      this.pdfLoading = false;
    }
  },
};
</script>

<style lang="postcss" scoped>
.fullpage {
  height: calc(100dvh - 60px); /* 60px is the height of toolbar */
}

.sidebar {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 20px;
  width: 100%;
  max-width: 260px;
  overflow: auto;
  padding: 20px 16px 32px;

  background-color: #fff;
  border-right: 1px solid var(--gray-300);
  box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.15);
}

@media (max-width: 860px) {
  .sidebar {
  }
}

.pdf-container {
  flex: auto;
  overflow-y: auto;
  position: relative;
  min-width: 0;
}

.toolbar {
  flex: none;
  box-sizing: border-box;
  align-items: center;
  display: flex;
  gap: 16px;
  height: 56px;
  justify-content: space-between;
  padding: 0 16px;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--gray-100);
  border-bottom: 1px solid var(--gray-300);
}

.toolbar__pageNavigation {
  display: flex;
  justify-content: center;
  min-width: calc(100% - 360px);

  & >>> sc-button[is-disabled] {
    pointer-events: none;
  }
}

.toolbar__pageActions {
  width: 360px;
}

.toolbar input {
  height: 30px;
  padding: 0;
  width: 40px;

  border: 0;
  border-radius: 2px;
  outline: 1px solid var(--gray-300);
  background-color: var(--gray-0);

  font-family: var(--font-family-sans);
  line-height: 32px;
  text-align: center;
}

.toolbar input:active,
.toolbar input:focus {
  outline: 1px solid var(--blue-400);
}

.page-container {
  flex: auto;
  width: 100%;
  min-height: 0;
}

.scrollable-container {
  overflow: auto;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
}

.pdf-page-container {
  position: relative;
  height: max-content;
  max-width: 1000px;
  margin: 0 auto;
  flex-direction: column;
}

.pdf {
  position: relative;
  padding: 0;
  max-width: 1000px;
  width: 100%;
}

.page-list {
  padding-left: 0px;
}

.page-list li {
  list-style: none;
  padding: 8px 4px;
  border-bottom: 1px solid #eee;
}

.page-list li a {
  color: #444;
  text-decoration: none;
}

.page-list li a i {
  color: #444;
}

.page-list li.errored a,
.page-list li.errored a i,
.page-list li.unreviewed a,
.page-list li.unreviewed a i {
  color: var(--red-700);
}

.page-list li.selected {
  background-color: rgba(0, 0, 0, 0.07);
  border-radius: 4px;
}

.sidebar--right {
  box-sizing: border-box;
  overflow-y: auto;
  max-width: 320px;
  width: 100%;
  padding: 16px;
  padding-bottom: 100px;

  background-color: #fff;
  border-left: 1px solid var(--gray-200);
}

.sidebar--right * {
  box-sizing: border-box !important;
}

.form-control {
  margin-top: 16px;

  & >>> .v-text-field {
  }

  & >>> .v-text-field .v-input__slot {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid #ddd;
    border-radius: 8px;
  }
}

.form-control .error--text {
  & >>> .v-input__slot {
    border: 1px solid var(--red-500) !important;
  }
}

.voucher-id-autocomplete {
  & >>> .v-select__selections {
    min-width: 0;
  }
}

.upload-voucher-message-container {
  max-width: 700px;
  width: 100%;
  flex-shrink: 1;
  min-width: 0;
  font-size: 15px;
}

.outer-container {
  min-height: 0;
}

.inner-container {
  min-height: 0;
}

.filing-instructions-switch {
  flex: none;
}

.v-alert.ai-suggestion {
  border-radius: 8px;
  padding: 10px 16px;
  background: linear-gradient(#2aa2f9, #2aa2f9) padding-box,
    linear-gradient(
        135deg,
        #667eea 0%,
        #764ba2 25%,
        #f093fb 50%,
        #4facfe 75%,
        #00f2fe 100%
      )
      border-box;
  border: 2px solid transparent;
}

.v-alert.analyzing-filing-instructions-alert {
  border-radius: 8px;
  padding: 10px 16px;
  background: linear-gradient(#2aa2f9, #2aa2f9) padding-box,
    linear-gradient(
        135deg,
        #667eea 0%,
        #764ba2 25%,
        #f093fb 50%,
        #4facfe 75%,
        #00f2fe 100%
      )
      border-box;
  border: 2px solid transparent;
}
</style>

<style lang="postcss">
.voucher-id-autocomplete-menu .v-list__tile {
  height: auto !important;
  min-height: auto !important;
  border-bottom: 1px solid #eee;
  padding: 8px 12px;
}
</style>
